// Typed fetch client for the DevConf Companion API. The API is served same-origin under
// /api by a Vite plugin (vite-plugin-mock-api.js), which precomputes the static dataset
// at server start and serves it in dev and preview; set VITE_API_BASE to point at a
// different origin (e.g. an S3/CDN deployment) without touching this file. The @returns
// of the convenience functions reference the domain types in ./types, generated from
// mock/openapi.yaml.

export const API_BASE = import.meta.env.VITE_API_BASE ?? "/api";

// On a dumb static file host a directory and a file cannot share a name, so the API is
// baked as a tree of `.json` files (`sessions.json` + `sessions/<id>.json`) instead of the
// extensionless `/api` the Vite plugin serves. Set VITE_API_STATIC=1 at build time to make
// every resource path request its `.json` file; unset for the same-origin plugin.
const STATIC_JSON = import.meta.env.VITE_API_STATIC === "1";

export class ApiError extends Error {
  constructor(message, status, url) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.url = url;
  }
}

function toUrl(path) {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  const rel = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE}${rel}${STATIC_JSON ? ".json" : ""}`;
}

// Returns the parsed body as `unknown`; the typed surface is the convenience functions
// below.
export async function fetchJson(path, opts = {}) {
  const url = toUrl(path);
  let res;
  try {
    res = await fetch(url, { headers: { Accept: "application/json" }, signal: opts.signal });
  } catch (err) {
    // Re-throw aborts unchanged so callers can distinguish cancellation from failure.
    if (err instanceof DOMException && err.name === "AbortError") {
      throw err;
    }
    const message = err instanceof Error ? err.message : "Network request failed";
    throw new ApiError(message, 0, url);
  }

  if (!res.ok) {
    throw new ApiError(`Request failed (${res.status} ${res.statusText})`, res.status, url);
  }

  return await res.json();
}

// Aborts are never retried.
export async function retry(fn, { attempts = 3, baseDelayMs = 200 } = {}) {
  let lastError;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        throw err;
      }
      lastError = err;
      if (i < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, baseDelayMs * 2 ** i));
      }
    }
  }
  throw lastError;
}

export async function getTracks(opts) {
  return  (await fetchJson("/tracks", opts));
}

export async function getRooms(opts) {
  return  (await fetchJson("/rooms", opts));
}

export async function getSpeakers(opts) {
  return  (await fetchJson("/speakers", opts));
}

export async function getConference(opts) {
  return  (await fetchJson("/conference", opts));
}

// The full session list as PLAIN records (no embedded relations); the app holds the
// tracks/rooms/speakers collections separately and joins client-side.
export async function getSessions(opts) {
  return  (await fetchJson("/sessions", opts));
}

// One session with its `track` and `room` relations embedded by the API. Speakers are a
// many-relation (speakerIds[]) that isn't embedded, so the detail view resolves those
// from the separately-fetched speakers list.
export async function getSessionDetail(id, opts) {
  return  (await fetchJson(`/sessions/${id}`, opts));
}
